import { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { Layout } from '../components/layout';
import { ArrowRight } from 'lucide-react';

const SITE_URL = 'https://www.patekarecycle.com';

interface BlogPost {
  id: string;
  title: string;
  excerpt: string;
  date: string;
  readTime: string;
  category: string;
  author: string;
  image?: string;
}

const CATEGORIES = ['All', 'Compliance', 'Regulation', 'E-Waste', 'Plastic Waste', 'Traceability', 'Industry Insights'];

const categoryColors: Record<string, string> = {
  Compliance: 'text-blue-600',
  Regulation: 'text-purple-600',
  'E-Waste': 'text-orange-600',
  'Plastic Waste': 'text-green-600',
  Traceability: 'text-teal-600',
  'Industry Insights': 'text-gray-600',
  ESG: 'text-emerald-600',
  'Circular Economy': 'text-cyan-600',
};

export function Blog() {
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState('All');

  useEffect(() => {
    fetch('/data/blog.json')
      .then((res) => res.json())
      .then((data) => {
        setPosts(data.posts);
        setLoading(false);
      })
      .catch((err) => {
        console.error('Failed to load blog posts:', err);
        setLoading(false);
      });
  }, []);

  const filtered =
    activeCategory === 'All' ? posts : posts.filter((p) => p.category === activeCategory);

  // Collect all categories present in data
  const presentCategories = ['All', ...Array.from(new Set(posts.map((p) => p.category)))];
  const displayCategories = CATEGORIES.filter((c) => presentCategories.includes(c));

  return (
    <Layout>
      <Helmet>
        <title>Insights | Punarvaa - Waste Management & Recycling</title>
        <meta name="title" content="Insights | Punarvaa - Waste Management & Recycling" />
        <meta
          name="description"
          content="Latest insights on waste management, EPR compliance, resource recovery, circular economy solutions, and sustainability practices from Punarvaa experts."
        />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href={`${SITE_URL}/blog`} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={`${SITE_URL}/blog`} />
        <meta property="og:title" content="Insights | Punarvaa" />
        <meta
          property="og:description"
          content="Latest insights on waste management, EPR compliance, resource recovery, and circular economy solutions."
        />
        <meta property="og:site_name" content="Punarvaa by Patekar Recycle" />
        <meta property="og:locale" content="en_IN" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Insights | Punarvaa" />
        <meta
          name="twitter:description"
          content="Latest insights on waste management, EPR compliance, resource recovery, and circular economy."
        />
      </Helmet>

      {/* Page header */}
      <section className="pt-28 pb-12 bg-white border-b border-gray-100">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs font-semibold tracking-widest uppercase text-gray-400 mb-4">
            Insights
          </p>
          <h1 className="text-5xl sm:text-6xl font-bold text-gray-900 mb-4">
            Insights
          </h1>
          <p className="text-lg text-gray-500 max-w-xl">
            Perspectives on waste management, compliance, and the circular economy in India.
          </p>
        </div>
      </section>

      {/* Category filter */}
      <section className="py-6 bg-white border-b border-gray-100 sticky top-16 z-40">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-6 overflow-x-auto scrollbar-hide">
            {displayCategories.map((cat) => (
              <button
                key={cat}
                onClick={() => setActiveCategory(cat)}
                className={`text-sm whitespace-nowrap transition-colors pb-0.5 border-b-2 ${
                  activeCategory === cat
                    ? 'text-p-600 border-p-600 font-medium'
                    : 'text-gray-500 border-transparent hover:text-gray-900'
                }`}
              >
                {cat}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Posts */}
      <section className="py-12 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-4xl">
          {loading && (
            <div className="space-y-0">
              {[1, 2, 3].map((i) => (
                <div key={i} className="py-8 border-b border-gray-100 animate-pulse">
                  <div className="h-3 bg-gray-100 rounded w-20 mb-4" />
                  <div className="h-6 bg-gray-100 rounded w-3/4 mb-3" />
                  <div className="h-4 bg-gray-100 rounded w-full mb-2" />
                  <div className="h-4 bg-gray-100 rounded w-2/3" />
                </div>
              ))}
            </div>
          )}

          {!loading && filtered.length === 0 && (
            <p className="py-16 text-center text-gray-400">No posts in this category yet.</p>
          )}

          {!loading && filtered.length > 0 && (
            <div>
              {filtered.map((post) => (
                <article key={post.id} className="py-8 border-b border-gray-100 group">
                  <div className="flex flex-col sm:flex-row sm:items-start gap-6">
                    <div className="flex-1">
                      {/* Category + meta */}
                      <div className="flex items-center gap-4 mb-3">
                        <span
                          className={`text-xs font-semibold uppercase tracking-widest ${
                            categoryColors[post.category] || 'text-gray-500'
                          }`}
                        >
                          {post.category}
                        </span>
                        <span className="text-xs text-gray-400">
                          {new Date(post.date).toLocaleDateString('en-IN', {
                            day: 'numeric',
                            month: 'short',
                            year: 'numeric',
                          })}
                        </span>
                        <span className="text-xs text-gray-400">{post.readTime}</span>
                      </div>

                      {/* Title */}
                      <h2 className="text-xl font-bold text-gray-900 mb-2 group-hover:text-p-600 transition-colors leading-snug">
                        <Link to={`/blog/${post.id}`}>{post.title}</Link>
                      </h2>

                      {/* Excerpt */}
                      <p className="text-sm text-gray-500 leading-relaxed mb-4 line-clamp-2">
                        {post.excerpt}
                      </p>

                      {/* Footer */}
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-gray-400">{post.author}</span>
                        <Link
                          to={`/blog/${post.id}`}
                          className="inline-flex items-center gap-1.5 text-sm font-medium text-p-600 hover:text-p-700 transition-colors"
                        >
                          Read more
                          <ArrowRight className="w-3.5 h-3.5" />
                        </Link>
                      </div>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          )}
        </div>
      </section>
    </Layout>
  );
}
